import { useMemo } from 'react';
import { Pencil, Trash2 } from 'lucide-react';

import { ProtectedComponent } from '@/components/ProtectedComponent';
import { Checkbox } from '@/components/ui/checkbox';
import { DropdownAction } from '@/components/ui/dropdown-actions';
import { DropdownMenuItem, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';

import { useToggle } from '@/hooks/use-toggle';
import { useCurrentUser } from '@/hooks/useCurrentUser';

import { useDeleteRoleMutation } from '@/query/role.query';

import { Permissions } from '@/constants';

import { RoleUpdate } from '../../RoleUpdate/RoleUpdate';

import useAlertStore from '@/store/alert.store';
import type { DropdownOption } from '@/types/common.type';
import type { Role } from '@/types/role.type';

type Props = {
  role: Role;
  permissions: DropdownOption[];
};

export const RolePermission = ({ role, permissions, ...props }: Props) => {
  const { state, toggle } = useToggle();
  const { userData } = useCurrentUser();

  const deleteRoleMutation = useDeleteRoleMutation();
  const openAlert = useAlertStore(s => s.openAlert);

  const selectedPermissions = useMemo<DropdownOption[]>(() => {
    return role.permissions?.map(p => ({ label: p.label, value: p.id.toString() })) || [];
  }, [role.permissions]);

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    openAlert({
      title: 'Delete Batch Call',
      description: `Are you sure you want to delete "${role.name}"? This action cannot be undone.`,
      type: 'destructive',
      confirmText: 'Delete',
      onConfirm: () => deleteRoleMutation.mutate(role.id),
    });
  };

  return (
    <>
      <tr className="border-b align-top" {...props}>
        <td className="p-3 font-medium">{role.name}</td>
        <td className="p-3">{role.description}</td>
        <td className="p-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {permissions?.map(permission => (
              <label
                key={permission.value}
                htmlFor={`role-${role.id}-perm-${permission.value}`}
                className="flex items-center gap-2"
              >
                <Checkbox
                  id={`role-${role.id}-perm-${permission.value}`}
                  checked={selectedPermissions.some(rp => rp.value === permission.value)}
                />
                <span className="text-sm">{permission.label}</span>
              </label>
            ))}
          </div>
        </td>
        {userData?.role.id !== role.id && (
          <ProtectedComponent
            requiredPermissions={[Permissions.RolesCreateUpdate, Permissions.RolesDelete]}
          >
            <td className="p-3">
              <DropdownAction>
                <ProtectedComponent requiredPermissions={[Permissions.RolesCreateUpdate]}>
                  <DropdownMenuItem variant="default" onClick={toggle}>
                    <Pencil className="h-4 w-4" />
                    Update Role
                  </DropdownMenuItem>
                </ProtectedComponent>
                <ProtectedComponent requiredPermissions={[Permissions.RolesDelete]}>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem variant="destructive" onClick={handleDelete}>
                    <Trash2 className="h-4 w-4" />
                    Delete Role
                  </DropdownMenuItem>
                </ProtectedComponent>
              </DropdownAction>
            </td>
          </ProtectedComponent>
        )}
      </tr>

      <RoleUpdate isOpen={state} toggle={toggle} roleId={role.id} />
    </>
  );
};
